import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Bike, ArrowLeft, User } from 'lucide-react';
import { isFirebaseConfigured, getDB } from '../lib/firebase';
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  query, 
  where,
  orderBy,
  serverTimestamp,
  setDoc
} from 'firebase/firestore';
import { COLLECTIONS, ClientAssignment } from '../lib/database.types';
import { AddressInput } from './AddressInput';
import { CLIENT_DISPLAY_NAMES } from '../features/cases/types';
import { useAuth } from '../contexts/AuthContext';

export function ClientData() {
  const { id: caseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [caseData, setCaseData] = useState<any>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [useDeliveryAsPostal, setUseDeliveryAsPostal] = useState(false);
  const [addressDetails, setAddressDetails] = useState<{
    postal?: { address: string; lat?: number; lng?: number };
    delivery?: { address: string; lat?: number; lng?: number };
  }>({});
  const [availableBikes, setAvailableBikes] = useState<Array<any>>([]);
  const [selectedBikeId, setSelectedBikeId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // isNewCase will be determined by fetchCaseData based on whether data exists for the caseId
  const [isNewCase, setIsNewCase] = useState(false);

  // Helper functions
  const createMockCase = () => ({
    id: caseId,
    not_at_fault_name: '',
    claim_number: '',
    not_at_fault_insurance_company: '',
    not_at_fault_rego: '',
    at_fault_name: '',
    at_fault_policy_number: '',
    at_fault_insurance_company: '',
    at_fault_rego: '',
    postal_address: '',
    delivery_address: '',
    delivery_coordinates: null,
    bike_id: null,
    collecting_for_client: user?.hasFullAccess ? ClientAssignment.CRAIG : (user?.clientType || ClientAssignment.CRAIG),
    status: 'new_matter',
    created_at: new Date(),
    updated_at: new Date()
  });

  const generateReferenceNumber = () => {
    return `REF-${Date.now()}`;
  };

  const generateCaseNumber = () => {
    return `CASE-${Date.now()}`;
  };

  // Determine collecting for client based on user
  const collectingForClient = user?.hasFullAccess 
    ? ClientAssignment.CRAIG 
    : (user?.clientType || ClientAssignment.CRAIG);

  useEffect(() => {
    if (caseId) {
      // Always attempt to fetch case data when we have a caseId
      fetchCaseData(caseId);
      fetchAvailableBikes();
    }
  }, [caseId]);

  // Case numbers are automatically generated by the database via triggers

  const initializeNewCase = async (uuid: string) => {
    if (!uuid) {
      console.error('No UUID provided for new case initialization');
      return;
    }

    try {
      setLoading(true);
      
      // Determine the client type based on user permissions
      const clientType = user?.hasFullAccess 
        ? ClientAssignment.CRAIG 
        : (user?.clientType || ClientAssignment.CRAIG);
      
      // Initialize with default values for new case
      setCaseData({
        id: uuid,
        not_at_fault_name: '',
        claim_number: '',
        not_at_fault_insurance_company: '',
        not_at_fault_rego: '',
        at_fault_name: '',
        at_fault_policy_number: '',
        at_fault_insurance_company: '',
        at_fault_rego: '',
        postal_address: '',
        delivery_address: '',
        delivery_coordinates: null,
        bike_id: null,
        collecting_for_client: clientType,
        status: 'new_matter'
      });
      
      // Reset related states
      setAddressDetails({});
      setSelectedBikeId(null);
      setUseDeliveryAsPostal(false);
    } catch (error) {
      console.error('Error initializing new case with UUID:', uuid, error);
      alert('Failed to initialize new case. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCaseData = async (id: string) => {
    if (!isFirebaseConfigured()) {
      // Mock data for development
      const mockCase = createMockCase();
      setCaseData(mockCase);
      setIsNewCase(true);
      setLoading(false);
      return;
    }

    try {
      const caseDocRef = doc(getDB(), COLLECTIONS.CASES, id);
      const caseDoc = await getDoc(caseDocRef);
      
      if (caseDoc.exists()) {
        const data = caseDoc.data();
        // Convert Firestore timestamp to Date object
        const caseData = {
          ...data,
          id: caseDoc.id,
          created_at: data.created_at?.toDate ? data.created_at.toDate() : new Date(data.created_at),
          updated_at: data.updated_at?.toDate ? data.updated_at.toDate() : new Date(data.updated_at)
        };
        setCaseData(caseData);
        setIsNewCase(false);
        console.log('Fetched case data:', caseData);
      } else {
        // Case doesn't exist, initialize as new case
        const mockCase = createMockCase();
        setCaseData(mockCase);
        setIsNewCase(true);
        console.log('No existing case found, initializing new case');
      }
    } catch (error) {
      console.error('Error fetching case:', error);
      const mockCase = createMockCase();
      setCaseData(mockCase);
      setIsNewCase(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableBikes = async () => {
    if (!isFirebaseConfigured()) {
      // Mock data for development
      setAvailableBikes([
        {
          id: 'mock1',
          make: 'Honda',
          model: 'CBR300R',
          rego_number: 'ABC123',
          status: 'available'
        }
      ]);
      return;
    }

    try {
      const bikesQuery = query(
        collection(getDB(), COLLECTIONS.BIKES),
        where('status', '==', 'available')
      );
      const bikesSnapshot = await getDocs(bikesQuery);
      const bikes = bikesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAvailableBikes(bikes);
    } catch (error) {
      console.error('Error fetching bikes:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!caseId) {
      alert('Error: No case ID provided');
      return;
    }

    setIsSaving(true);

    try {
      if (!isFirebaseConfigured()) {
        // Mock save for development
        console.log('Mock: Would save case data:', caseData);
        alert('Case saved successfully (mock mode)!');
        navigate('/case-management');
        return;
      }

      const caseFormData = {
        ...caseData,
        bike_id: selectedBikeId,
        collecting_for_client: collectingForClient,
        reference_number: caseData.reference_number || generateReferenceNumber(),
        status: 'open',
        case_number: caseData.case_number || generateCaseNumber()
      };

      if (isNewCase) {
        // Creating a new case - use the provided UUID as the document ID
        console.log('Creating new case with data:', { ...caseFormData, id: caseId });
        const caseDocRef = doc(getDB(), COLLECTIONS.CASES, caseId);
        await setDoc(caseDocRef, {
          ...caseFormData,
          created_at: serverTimestamp(),
          updated_at: serverTimestamp()
        });
      } else {
        // Updating existing case
        console.log('Updating existing case with data:', caseFormData);
        const caseDocRef = doc(getDB(), COLLECTIONS.CASES, caseId);
        await updateDoc(caseDocRef, {
          ...caseFormData,
          updated_at: serverTimestamp()
        });
      }

      // Update bike assignment if changed
      if (selectedBikeId && selectedBikeId !== caseData?.bike_id) {
        // Mark new bike as delivered
        await updateDoc(doc(getDB(), COLLECTIONS.BIKES, selectedBikeId), { 
          status: 'delivered' 
        });
        
        // Mark old bike as available (if there was one)
        if (caseData?.bike_id) {
          await updateDoc(doc(getDB(), COLLECTIONS.BIKES, caseData.bike_id), { 
            status: 'available' 
          });
        }
      }

      alert('Case saved successfully!');
      navigate('/case-management');
    } catch (error: any) {
      console.error('Error saving case:', error);
      alert(`Failed to save case: ${error.message}`);
    } finally {
      setIsSaving(false);
    }  
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    console.log('Input change:', name, '=', value); // Debug log
    setCaseData((prev: any) => {
      const updated = { ...prev, [name]: value };
      console.log('Updated caseData:', updated); // Debug log
      return updated;
    });
  };

  // Address selection is now handled directly in the onChange handlers

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (!caseData && !isNewCase) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Case not found</h3>
        <button 
          onClick={() => navigate('/case-management')}
          className="mt-4 px-4 py-2 bg-[#6563ff] text-white rounded-lg hover:bg-[#5451ff] transition-colors"
        >
          Back to Cases
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="p-6">
        <div className="mb-6 flex items-center">
          <button 
            onClick={() => navigate('/case-management')} 
            className="mr-4 flex items-center text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft className="h-5 w-5 mr-1" />
            <span>Back to Cases</span>
          </button>
          <h1 className="text-2xl font-bold">
            {isNewCase ? 'Create New Case' : 'Client Data'}
          </h1>
          {!isNewCase && caseData?.case_number && (
            <span className="ml-4 px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">
              Case #{caseData.case_number}
            </span>
          )}
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {/* Not at Fault Party */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Not at Fault Party</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="not_at_fault_name" className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  id="not_at_fault_name"
                  name="not_at_fault_name"
                  value={caseData.not_at_fault_name || ''}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#6563ff] focus:border-[#6563ff]"
                />
              </div>
              <div>
                <label htmlFor="claim_number" className="block text-sm font-medium text-gray-700 mb-1">
                  Claim Number
                </label>
                <input
                  type="text"
                  id="claim_number"
                  name="claim_number"
                  value={caseData.claim_number || ''}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#6563ff] focus:border-[#6563ff]"
                />
              </div>
              <div>
                <label htmlFor="not_at_fault_insurance_company" className="block text-sm font-medium text-gray-700 mb-1">
                  Insurance Company
                </label>
                <input
                  type="text"
                  id="not_at_fault_insurance_company"
                  name="not_at_fault_insurance_company"
                  value={caseData.not_at_fault_insurance_company || ''}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#6563ff] focus:border-[#6563ff]"
                />
              </div>
              <div>
                <label htmlFor="not_at_fault_rego" className="block text-sm font-medium text-gray-700 mb-1">
                  Registration
                </label>
                <input
                  type="text"
                  id="not_at_fault_rego"
                  name="not_at_fault_rego"
                  value={caseData.not_at_fault_rego || ''}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#6563ff] focus:border-[#6563ff]"
                />
              </div>
            </div>
          </div>

          {/* At Fault Party */}
          <div className="mb-6 border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">At Fault Party</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="at_fault_name" className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  id="at_fault_name"
                  name="at_fault_name"
                  value={caseData.at_fault_name || ''}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#6563ff] focus:border-[#6563ff]"
                />
              </div>
              <div>
                <label htmlFor="at_fault_policy_number" className="block text-sm font-medium text-gray-700 mb-1">
                  Policy Number
                </label>
                <input
                  type="text"
                  id="at_fault_policy_number"
                  name="at_fault_policy_number"
                  value={caseData.at_fault_policy_number || ''}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#6563ff] focus:border-[#6563ff]"
                />
              </div>
              <div>
                <label htmlFor="at_fault_insurance_company" className="block text-sm font-medium text-gray-700 mb-1">
                  Insurance Company
                </label>
                <input
                  type="text"
                  id="at_fault_insurance_company"
                  name="at_fault_insurance_company"
                  value={caseData.at_fault_insurance_company || ''}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#6563ff] focus:border-[#6563ff]"
                />
              </div>
              <div>
                <label htmlFor="at_fault_rego" className="block text-sm font-medium text-gray-700 mb-1">
                  Registration
                </label>
                <input
                  type="text"
                  id="at_fault_rego"
                  name="at_fault_rego"
                  value={caseData.at_fault_rego || ''}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#6563ff] focus:border-[#6563ff]"
                />
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div className="mb-6 border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Address Information</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Delivery Address
                </label>
                <AddressInput
                  value={addressDetails.delivery?.address || ''}
                  onChange={(address: string) => {
                    setAddressDetails((prev: any) => ({
                      ...prev,
                      delivery: { 
                        address: address,
                        lat: prev.delivery?.lat,
                        lng: prev.delivery?.lng
                      }
                    }));
                    
                    if (useDeliveryAsPostal) {
                      setAddressDetails((prev: any) => ({
                        ...prev,
                        postal: { 
                          address: address,
                          lat: undefined,
                          lng: undefined
                        }
                      }));
                    }
                  }}
                  placeholder="Enter delivery address..."
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="use_delivery_as_postal"
                  checked={useDeliveryAsPostal}
                  onChange={(e) => setUseDeliveryAsPostal(e.target.checked)}
                  className="h-4 w-4 text-[#6563ff] focus:ring-[#6563ff] border-gray-300 rounded"
                />
                <label htmlFor="use_delivery_as_postal" className="ml-2 block text-sm text-gray-700">
                  Use delivery address as postal address
                </label>
              </div>
              {!useDeliveryAsPostal && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Postal Address
                  </label>
                  <AddressInput
                    value={addressDetails.postal?.address || ''}
                    onChange={(address: string) => {
                      setAddressDetails((prev: any) => ({
                        ...prev,
                        postal: { 
                          address: address,
                          lat: undefined,
                          lng: undefined
                        }
                      }));
                    }}
                    placeholder="Enter postal address..."
                  />
                </div>
              )}
            </div>
          </div>

          {/* Collecting For Client Assignment */}
          <div className="mb-6 border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <User className="h-5 w-5 mr-2 text-indigo-600" />
              Collecting For Client
            </h3>
            <div>
              <label htmlFor="collecting_for_client" className="block text-sm font-medium text-gray-700 mb-2">
                Portal Assignment <span className="text-red-500">*</span>
              </label>
              <select
                id="collecting_for_client"
                name="collecting_for_client"
                value={caseData.collecting_for_client || ClientAssignment.CRAIG}
                onChange={(e) => {
                  setCaseData((prev: any) => ({
                    ...prev,
                    collecting_for_client: e.target.value as ClientAssignment
                  }));
                }}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#6563ff] focus:border-[#6563ff]"
                required
              >
                {Object.entries(CLIENT_DISPLAY_NAMES).map(([key, displayName]) => (
                  <option key={key} value={key}>
                    {displayName}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                This determines which client portal can view and manage this case
              </p>
              {user?.hasFullAccess && (
                <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs text-amber-700">
                    <strong>Admin Note:</strong> As an administrator, you can assign cases to any client portal. 
                    Regular clients can only create cases for their own organization.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Bike Assignment Section */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Bike Assignment</h3>
            
            {caseData.bike ? (
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <h4 className="font-medium text-gray-900 mb-2">Currently Assigned Bike</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Make & Model</p>
                    <p className="font-medium">{caseData.bike.make} {caseData.bike.model}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Registration</p>
                    <p className="font-medium">{caseData.bike.rego_number}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Daily Rate</p>
                    <p className="font-medium">${caseData.bike.rate_a}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4">
                  {availableBikes.length > 0 ? (
                    availableBikes.map((bike: any) => (
                      <div 
                        key={bike.id}
                        className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                          selectedBikeId === bike.id 
                            ? 'border-[#6563ff] bg-[#6563ff]/5' 
                            : 'border-gray-200 hover:border-[#6563ff] hover:bg-gray-50'
                        }`}
                        onClick={() => setSelectedBikeId(bike.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <Bike className={`h-6 w-6 ${selectedBikeId === bike.id ? 'text-[#6563ff]' : 'text-gray-400'}`} />
                            <div>
                              <p className="font-medium text-gray-900">{bike.make} {bike.model}</p>
                              <p className="text-sm text-gray-500">Rego: {bike.rego_number}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-4">
                            <div className="text-right">
                              <p className="text-sm text-gray-600">Rate A</p>
                              <p className="font-medium text-gray-900">${bike.rate_a}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-gray-600">Rate B</p>
                              <p className="font-medium text-gray-900">${bike.rate_b}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4 text-gray-500">
                      No bikes available for assignment
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-4 pt-4 border-t mt-6">
            <button
              type="button"
              onClick={() => navigate('/case-management')}
              disabled={isSaving}
              className="px-4 py-2 text-gray-700 hover:text-gray-900 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2 bg-[#6563ff] text-white rounded-lg hover:bg-[#5451ff] transition-colors disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : isNewCase ? 'Create Case' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
